import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import socket from '../services/socket';
import '../styles/AdminMacroGame.css';
import AdminLEDButtons from './AdminLEDButtons';

const GAME_NAME = 'A Brief Case';
const GAME_ICON = '💼';
const GAME_DESCRIPTION = 'Jeopardy earns the right to pick a briefcase. Only briefcase totals determine the winner!';

const QUICK_VALUES = [1, 2, 3, 4, 5];

const BRIEFCASE_TYPES = [
  { id: 'positive', emoji: '💰', label: '+', color: '#00ff88', hasValue: true },
  { id: 'negative', emoji: '💸', label: '-', color: '#ff4444', hasValue: true },
  { id: 'curtain', emoji: '🎭', label: 'Curtain', color: '#9b59b6', hasValue: true },
  { id: 'double', emoji: '✕2', label: 'Double', color: '#ffd700', hasValue: false },
  { id: 'half', emoji: '½', label: 'Half', color: '#ff8c00', hasValue: false },
  { id: 'swap', emoji: '🔄', label: 'Swap', color: '#00bfff', hasValue: false },
];

const AdminBriefcaseBlitz = () => {
  const [gameState, setGameState] = useState({
    active: false,
    teams: [],
    currentRound: 0,
    totalRounds: 50
  });
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [customValue, setCustomValue] = useState('');
  const [numTeamsToCreate, setNumTeamsToCreate] = useState(8);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [selectedBriefcaseType, setSelectedBriefcaseType] = useState('positive');
  const [tradingPaused, setTradingPaused] = useState(false);

  // Powerup activation state
  const [powerupMode, setPowerupMode] = useState(null); // { teamId, powerupIndex, type }
  // Swap mode: 3-step flow
  const [swapMode, setSwapMode] = useState(null); // { step, fromTeamId, fromBcIndex, toTeamId, toBcIndex }

  const getAdminToken = () => localStorage.getItem('adminKey') || '';

  const fetchGameState = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/briefcase/state`);
      if (response.data.success) {
        setGameState(response.data.state);
      }
    } catch (error) {
      console.error('Failed to fetch briefcase state:', error);
    }
  }, []);

  useEffect(() => {
    const handleStateUpdate = (data) => setGameState(data);
    const handleMarketUpdate = (data) => setTradingPaused(!data.tradingEnabled);

    socket.on('briefcase-state-update', handleStateUpdate);
    socket.on('market-state-update', handleMarketUpdate);
    fetchGameState();

    return () => {
      socket.off('briefcase-state-update', handleStateUpdate);
      socket.off('market-state-update', handleMarketUpdate);
    };
  }, [fetchGameState]);

  useEffect(() => {
    const fetchMarketState = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/admin/market/status`);
        setTradingPaused(!response.data.tradingEnabled);
      } catch (error) {
        console.error('Failed to fetch market state:', error);
      }
    };
    fetchMarketState();
  }, []);

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  const startGame = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/trading/pause`, {}, {
        headers: { 'x-admin-token': getAdminToken() }
      });
      const response = await axios.post(`${API_BASE_URL}/api/admin/briefcase/start`, {
        numTeams: numTeamsToCreate
      }, {
        headers: { 'x-admin-token': getAdminToken() }
      });
      if (response.data.success) {
        setGameState(response.data.state);
        showMessage(`Game started with ${numTeamsToCreate} teams!`, 'success');
        await axios.post(`${API_BASE_URL}/api/admin/events/broadcast`, {
          type: 'sprite:briefcase-run', duration: 10, eventName: 'A BRIEF CASE!'
        }, { headers: { 'x-admin-token': getAdminToken() } });
      }
    } catch (error) {
      showMessage(`Error: ${error.message}`, 'error');
    }
  };

  const endGame = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/admin/briefcase/end`, {}, {
        headers: { 'x-admin-token': getAdminToken() }
      });
      await axios.post(`${API_BASE_URL}/api/admin/trading/resume`, {}, {
        headers: { 'x-admin-token': getAdminToken() }
      });
      if (response.data.success) {
        showMessage('Game ended! Trading resumed.', 'success');
        await axios.post(`${API_BASE_URL}/api/admin/events/broadcast`, {
          type: 'briefcase:end', finalStandings: response.data.finalStandings
        }, { headers: { 'x-admin-token': getAdminToken() } });
      }
    } catch (error) {
      showMessage(`Error: ${error.message}`, 'error');
    }
  };

  const addTeam = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/admin/briefcase/team`, {
        action: 'add'
      }, { headers: { 'x-admin-token': getAdminToken() } });
      if (response.data.success) showMessage(`Added ${response.data.team.name}`, 'success');
    } catch (error) {
      showMessage(`Error: ${error.message}`, 'error');
    }
  };

  const updateTeamName = async (teamId, name) => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/briefcase/team`, { teamId, name }, {
        headers: { 'x-admin-token': getAdminToken() }
      });
    } catch (error) {
      console.error('Failed to update team name:', error);
    }
  };

  const removeTeam = async (teamId) => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/briefcase/team`, { teamId, action: 'remove' }, {
        headers: { 'x-admin-token': getAdminToken() }
      });
      showMessage('Team removed', 'success');
    } catch (error) {
      showMessage(`Error: ${error.message}`, 'error');
    }
  };

  const awardBriefcase = async (teamId, value, briefcaseType) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/admin/briefcase/award`, {
        teamId,
        value,
        briefcaseType,
        hiddenValue: briefcaseType === 'curtain' ? value : undefined
      }, { headers: { 'x-admin-token': getAdminToken() } });

      if (response.data.success) {
        const team = gameState.teams.find(t => t.id === teamId);
        const typeInfo = BRIEFCASE_TYPES.find(t => t.id === briefcaseType);
        const displayValue = briefcaseType === 'negative' ? `-${Math.abs(value)}` : `+${Math.abs(value)}`;
        const label = typeInfo?.hasValue ? `${typeInfo.emoji} ${displayValue}` : typeInfo?.label;
        showMessage(`Awarded ${label} to ${team?.name || 'Team'}!`, 'success');
      }
    } catch (error) {
      showMessage(`Error: ${error.message}`, 'error');
    }
  };

  const removeBriefcase = async (teamId, briefcaseIndex) => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/briefcase/remove`, { teamId, briefcaseIndex }, {
        headers: { 'x-admin-token': getAdminToken() }
      });
      showMessage('Briefcase removed', 'success');
    } catch (error) {
      showMessage(`Error: ${error.message}`, 'error');
    }
  };

  const activatePowerup = async (teamId, powerupIndex, targetBriefcaseIndex) => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/briefcase/activate-powerup`, {
        teamId, powerupIndex, targetBriefcaseIndex
      }, { headers: { 'x-admin-token': getAdminToken() } });
      showMessage('Powerup activated!', 'success');
      setPowerupMode(null);
    } catch (error) {
      showMessage(`Error: ${error.response?.data?.error || error.message}`, 'error');
    }
  };

  const executeSwap = async () => {
    if (!swapMode || swapMode.step !== 3) return;
    try {
      await axios.post(`${API_BASE_URL}/api/admin/briefcase/special`, {
        action: 'swap',
        fromTeamId: swapMode.fromTeamId,
        toTeamId: swapMode.toTeamId,
        fromBriefcaseIndex: swapMode.fromBcIndex,
        toBriefcaseIndex: swapMode.toBcIndex,
      }, { headers: { 'x-admin-token': getAdminToken() } });
      showMessage('SWAP executed!', 'success');
      setSwapMode(null);
    } catch (error) {
      showMessage(`Error: ${error.response?.data?.error || error.message}`, 'error');
    }
  };

  const resetScoresAndBoard = async () => {
    if (!window.confirm('Reset all team scores to 0 and clear the Jeopardy board?')) return;
    try {
      await axios.post(`${API_BASE_URL}/api/admin/briefcase/reset-scores`, {}, {
        headers: { 'x-admin-token': getAdminToken() }
      });
      showMessage('All scores and Jeopardy board reset!', 'success');
    } catch (error) {
      showMessage(`Error: ${error.message}`, 'error');
    }
  };

  const revealCurtains = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/briefcase/reveal-curtains`, {}, {
        headers: { 'x-admin-token': getAdminToken() }
      });
      showMessage('All curtains revealed!', 'success');
    } catch (error) {
      showMessage(`Error: ${error.message}`, 'error');
    }
  };

  const triggerSuspense = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/events/broadcast`, {
        type: 'briefcase:suspense', duration: 4, eventName: 'SUSPENSE...'
      }, { headers: { 'x-admin-token': getAdminToken() } });
      showMessage('Suspense animation triggered!', 'success');
    } catch (error) {
      showMessage(`Error: ${error.message}`, 'error');
    }
  };

  const sortedTeams = [...(gameState.teams || [])].sort((a, b) =>
    (b.totalValue || 0) - (a.totalValue || 0)
  );

  const formatCurrency = (value) => {
    if (typeof value === 'string') return value;
    if (value < 0) return `-${Math.abs(value)}`;
    return `${value}`;
  };

  const getBcDisplay = (bc) => {
    switch (bc.type) {
      case 'positive': return { emoji: '💰', label: `+${bc.value}`, color: '#00ff88' };
      case 'negative': return { emoji: '💸', label: `-${Math.abs(bc.value)}`, color: '#ff4444' };
      case 'curtain': return { emoji: bc.revealed ? '🎭' : '❓', label: bc.revealed ? `${bc.hiddenValue}` : '???', color: '#9b59b6' };
      case 'double': return { emoji: '✕2', label: bc.powerupUsed ? 'USED' : 'DOUBLE', color: bc.powerupUsed ? '#555' : '#ffd700' };
      case 'half': return { emoji: '½', label: bc.powerupUsed ? 'USED' : 'HALF', color: bc.powerupUsed ? '#555' : '#ff8c00' };
      case 'swap': return { emoji: '🔄', label: bc.powerupUsed ? 'USED' : 'SWAP', color: bc.powerupUsed ? '#555' : '#00bfff' };
      default: return { emoji: '💼', label: `${bc.value}`, color: '#2575fc' };
    }
  };

  const currentBcType = BRIEFCASE_TYPES.find(t => t.id === selectedBriefcaseType);

  return (
    <div className="admin-macro-game admin-briefcase-blitz">
      <div className="game-header">
        <h2>{GAME_ICON} {GAME_NAME}</h2>
        <p className="game-description">{GAME_DESCRIPTION}</p>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}

      {/* Game Status */}
      <div className="status-bar">
        <div className={`status-item ${gameState.active ? 'active' : 'inactive'}`}>
          <span className="status-label">Game:</span>
          <span className="status-value">{gameState.active ? 'ACTIVE' : 'NOT STARTED'}</span>
        </div>
        <div className={`status-item ${tradingPaused ? 'paused' : 'active'}`}>
          <span className="status-label">Trading:</span>
          <span className="status-value">{tradingPaused ? 'PAUSED' : 'ACTIVE'}</span>
        </div>
        {gameState.active && (
          <>
            <div className="status-item">
              <span className="status-label">Teams:</span>
              <span className="status-value">{gameState.teams?.length || 0}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Round:</span>
              <span className="status-value">{gameState.currentRound}/{gameState.totalRounds}</span>
            </div>
          </>
        )}
      </div>

      {/* Main Controls */}
      <div className="control-section">
        <h3>Game Controls</h3>
        <div className="button-row">
          {!gameState.active ? (
            <>
              <div className="input-group">
                <label>Teams:</label>
                <input
                  type="number" min="1" max="20"
                  value={numTeamsToCreate}
                  onChange={(e) => setNumTeamsToCreate(parseInt(e.target.value) || 8)}
                  style={{ width: '60px' }}
                />
              </div>
              <button className="btn primary large" onClick={startGame}>
                🚀 Start {GAME_NAME}
              </button>
            </>
          ) : (
            <>
              <button className="btn" onClick={addTeam}>➕ Add Team</button>
              <button className="btn warning" onClick={triggerSuspense}>💓 Suspense</button>
              <button className="btn" style={{ background: '#9b59b6' }} onClick={revealCurtains}>
                🎭 Reveal Curtains
              </button>
              <button className="btn" style={{ background: '#ff8c00' }} onClick={resetScoresAndBoard}>
                🔄 Reset Scores & Board
              </button>
              <button className="btn danger large" onClick={endGame}>🛑 End Game</button>
            </>
          )}
          <button className="btn primary" onClick={() => window.open('/jeopardy/', '_blank')}>
            🎯 Open Jeopardy Board
          </button>
        </div>
      </div>

      {/* Powerup Activation Mode */}
      {powerupMode && (
        <div style={{
          background: 'rgba(255, 215, 0, 0.2)', border: '2px solid #ffd700',
          borderRadius: '8px', padding: '12px', marginBottom: '16px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              <strong>🎯 {powerupMode.type.toUpperCase()} MODE</strong> — Pick a briefcase from{' '}
              <strong>{gameState.teams.find(t => t.id === powerupMode.teamId)?.name}</strong> to apply
            </span>
            <button className="btn" onClick={() => setPowerupMode(null)}>❌ Cancel</button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
            {(gameState.teams.find(t => t.id === powerupMode.teamId)?.briefcases || []).map((bc, idx) => {
              const d = getBcDisplay(bc);
              const isPowerup = bc.type === 'double' || bc.type === 'half' || bc.type === 'swap';
              return (
                <button key={idx} className="btn" disabled={isPowerup}
                  style={{ background: isPowerup ? '#333' : d.color, color: '#000', opacity: isPowerup ? 0.4 : 1 }}
                  onClick={() => activatePowerup(powerupMode.teamId, powerupMode.powerupIndex, idx)}
                >
                  {d.emoji} {d.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Swap Mode */}
      {swapMode && (
        <div style={{
          background: 'rgba(0, 191, 255, 0.2)', border: '2px solid #00bfff',
          borderRadius: '8px', padding: '12px', marginBottom: '16px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              <strong>🔄 SWAP MODE</strong> — Step {swapMode.step}/3:{' '}
              {swapMode.step === 1 && 'Pick YOUR briefcase to swap away'}
              {swapMode.step === 2 && 'Pick TARGET TEAM, then their briefcase'}
              {swapMode.step === 3 && 'Confirm swap'}
            </span>
            <button className="btn" onClick={() => setSwapMode(null)}>❌ Cancel</button>
          </div>

          {swapMode.step === 1 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
              {(gameState.teams.find(t => t.id === swapMode.fromTeamId)?.briefcases || []).map((bc, idx) => {
                const d = getBcDisplay(bc);
                return (
                  <button key={idx} className="btn" style={{ background: d.color, color: '#000' }}
                    onClick={() => setSwapMode({ ...swapMode, step: 2, fromBcIndex: idx })}
                  >
                    {d.emoji} {d.label}
                  </button>
                );
              })}
            </div>
          )}

          {swapMode.step === 2 && (
            <div style={{ marginTop: '8px' }}>
              <p style={{ margin: '0 0 8px', color: '#aaa' }}>Pick a team and their briefcase:</p>
              {(gameState.teams || []).filter(t => t.id !== swapMode.fromTeamId).map(team => (
                <div key={team.id} style={{ marginBottom: '8px' }}>
                  <strong style={{ color: '#00bfff' }}>{team.name}:</strong>{' '}
                  {(team.briefcases || []).map((bc, idx) => {
                    const d = getBcDisplay(bc);
                    return (
                      <button key={idx} className="btn" style={{ background: d.color, color: '#000', margin: '2px' }}
                        onClick={() => setSwapMode({ ...swapMode, step: 3, toTeamId: team.id, toBcIndex: idx })}
                      >
                        {d.emoji} {d.label}
                      </button>
                    );
                  })}
                  {(!team.briefcases || team.briefcases.length === 0) && <span style={{ color: '#666' }}>No briefcases</span>}
                </div>
              ))}
            </div>
          )}

          {swapMode.step === 3 && (
            <div style={{ marginTop: '8px' }}>
              <p>
                Swapping: <strong>{gameState.teams.find(t => t.id === swapMode.fromTeamId)?.name}</strong> #{swapMode.fromBcIndex + 1}
                {' ↔ '}
                <strong>{gameState.teams.find(t => t.id === swapMode.toTeamId)?.name}</strong> #{swapMode.toBcIndex + 1}
              </p>
              <button className="btn primary" onClick={executeSwap}>✅ Confirm Swap</button>
            </div>
          )}
        </div>
      )}

      {/* Award UI */}
      {gameState.active && selectedTeam && !powerupMode && !swapMode && (
        <div className="control-section quick-award" style={{
          background: 'rgba(0, 255, 136, 0.1)', border: '2px solid #00ff88',
          borderRadius: '8px', padding: '12px', marginBottom: '16px'
        }}>
          <h3 style={{ margin: '0 0 8px 0' }}>
            Award to: <span style={{ color: '#00ff88' }}>{selectedTeam.name}</span>
          </h3>

          {/* Type selector */}
          <div className="button-row" style={{ marginBottom: '10px' }}>
            {BRIEFCASE_TYPES.map(bt => (
              <button key={bt.id} className="btn"
                style={{
                  background: selectedBriefcaseType === bt.id ? bt.color : 'rgba(255,255,255,0.1)',
                  color: selectedBriefcaseType === bt.id ? '#000' : '#fff',
                  fontWeight: selectedBriefcaseType === bt.id ? 'bold' : 'normal',
                  border: selectedBriefcaseType === bt.id ? `2px solid ${bt.color}` : '2px solid transparent'
                }}
                onClick={() => setSelectedBriefcaseType(bt.id)}
              >
                {bt.emoji} {bt.label}
              </button>
            ))}
          </div>

          {/* Value buttons for types that need values */}
          {currentBcType?.hasValue ? (
            <>
              <div className="button-row">
                {QUICK_VALUES.map(value => (
                  <button key={value} className="btn bonus"
                    style={{ background: currentBcType.color, color: '#000' }}
                    onClick={() => awardBriefcase(selectedTeam.id, value, selectedBriefcaseType)}
                  >
                    {selectedBriefcaseType === 'negative' ? '-' : '+'}{value}
                  </button>
                ))}
              </div>
              <div className="button-row" style={{ marginTop: '8px' }}>
                <input type="number" placeholder="Custom value" value={customValue}
                  onChange={(e) => setCustomValue(e.target.value)} style={{ width: '100px' }} />
                <button className="btn primary" onClick={() => {
                  if (customValue) {
                    awardBriefcase(selectedTeam.id, parseInt(customValue), selectedBriefcaseType);
                    setCustomValue('');
                  }
                }}>
                  Award Custom
                </button>
              </div>
            </>
          ) : (
            <div className="button-row">
              <button className="btn primary large"
                style={{ background: currentBcType?.color, color: '#000' }}
                onClick={() => awardBriefcase(selectedTeam.id, 0, selectedBriefcaseType)}
              >
                Award {currentBcType?.emoji} {currentBcType?.label}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Teams Grid */}
      {gameState.active && (
        <div className="teams-section">
          <h3>Teams ({gameState.teams?.length || 0})</h3>
          <div className="teams-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: '12px'
          }}>
            {sortedTeams.map((team, index) => (
              <div key={team.id}
                className={`team-card ${selectedTeam?.id === team.id ? 'selected' : ''}`}
                style={{
                  background: selectedTeam?.id === team.id ? 'rgba(0, 255, 136, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                  border: selectedTeam?.id === team.id ? '2px solid #00ff88' : '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px', padding: '12px', cursor: 'pointer', transition: 'all 0.2s'
                }}
                onClick={() => setSelectedTeam(selectedTeam?.id === team.id ? null : team)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#ffd700', fontWeight: 'bold' }}>#{index + 1}</span>
                    <input type="text" value={team.name}
                      onChange={(e) => {
                        const updatedTeams = gameState.teams.map(t =>
                          t.id === team.id ? { ...t, name: e.target.value } : t
                        );
                        setGameState({ ...gameState, teams: updatedTeams });
                      }}
                      onBlur={(e) => updateTeamName(team.id, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        background: 'transparent', border: 'none',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                        color: '#fff', fontSize: '14px', fontWeight: 'bold', width: '100px'
                      }}
                    />
                  </div>
                  <button className="btn-small danger"
                    onClick={(e) => { e.stopPropagation(); if (window.confirm(`Remove ${team.name}?`)) removeTeam(team.id); }}
                    style={{ padding: '2px 6px', fontSize: '12px' }}
                  >✕</button>
                </div>

                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#00ff88', marginBottom: '8px' }}>
                  {formatCurrency(team.totalValue || 0)}
                </div>

                {/* Briefcase chips */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px', minHeight: '24px' }}>
                  {(team.briefcases || []).map((bc, bcIndex) => {
                    const d = getBcDisplay(bc);
                    return (
                      <div key={bcIndex}
                        title={`${d.label} - Click to remove`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Remove ${d.emoji} ${d.label} from ${team.name}?`)) removeBriefcase(team.id, bcIndex);
                        }}
                        style={{
                          background: d.color, color: '#000',
                          padding: '2px 6px', borderRadius: '4px',
                          fontSize: '11px', fontWeight: 'bold', cursor: 'pointer'
                        }}
                      >
                        {d.emoji} {d.label}
                      </div>
                    );
                  })}
                </div>

                {/* Powerup activation buttons */}
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {(team.briefcases || []).map((bc, bcIndex) => {
                    if ((bc.type === 'double' || bc.type === 'half') && !bc.powerupUsed) {
                      return (
                        <button key={`pw-${bcIndex}`} className="btn-small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPowerupMode({ teamId: team.id, powerupIndex: bcIndex, type: bc.type });
                          }}
                          style={{ fontSize: '10px', padding: '2px 6px', background: getBcDisplay(bc).color, color: '#000' }}
                        >
                          Activate {bc.type === 'double' ? '✕2' : '½'}
                        </button>
                      );
                    }
                    if (bc.type === 'swap' && !bc.powerupUsed) {
                      return (
                        <button key={`pw-${bcIndex}`} className="btn-small"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Mark the swap powerup as used first by index, then start swap flow
                            setSwapMode({ step: 1, fromTeamId: team.id, swapPowerupIndex: bcIndex });
                          }}
                          style={{ fontSize: '10px', padding: '2px 6px', background: '#00bfff', color: '#000' }}
                        >
                          🔄 Use Swap
                        </button>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Leaderboard Summary */}
      {gameState.active && sortedTeams.length > 0 && (
        <div className="control-section" style={{ marginTop: '20px' }}>
          <h3>Leaderboard</h3>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px'
          }}>
            {sortedTeams.slice(0, 10).map((team, index) => (
              <div key={team.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 12px',
                background: index === 0 ? 'rgba(255, 215, 0, 0.2)' :
                  index === 1 ? 'rgba(192, 192, 192, 0.2)' :
                    index === 2 ? 'rgba(205, 127, 50, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                borderRadius: '6px'
              }}>
                <span>
                  <span style={{
                    fontWeight: 'bold',
                    color: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : index === 2 ? '#cd7f32' : '#888'
                  }}>#{index + 1}</span>
                  {' '}{team.name}
                </span>
                <span style={{ fontWeight: 'bold', color: '#00ff88' }}>
                  {formatCurrency(team.totalValue || 0)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="instructions-section" style={{ marginTop: '20px' }}>
        <h3>How to Run {GAME_NAME}</h3>
        <ol>
          <li><strong>Start game</strong> — Creates teams and pauses trading</li>
          <li><strong>Ask Jeopardy questions</strong> — Click "Open Jeopardy Board" to launch</li>
          <li><strong>Award briefcases manually</strong> — Click team card, pick type, pick value</li>
          <li><strong>Powerups</strong> — Double/Half: click "Activate" on team card, pick target briefcase. Swap: click "Use Swap", pick your briefcase, pick target team+briefcase</li>
          <li><strong>Curtains</strong> — Hidden until "Reveal Curtains" or end game</li>
          <li><strong>End game</strong> — Auto-reveals curtains, shows final standings</li>
        </ol>
        <p style={{ fontSize: '12px', color: '#888' }}>
          Tip: The embed leaderboard at <code>/embed/briefcase</code> shows live standings
        </p>
      </div>

      <AdminLEDButtons
        title="LED Scroll Events"
        events={[
          { label: 'Game Start', color: '#ffd700', type: 'sprite:briefcase-run', duration: 10, eventName: 'A BRIEF CASE!' },
          { label: 'Question', color: '#2196f3', type: 'game:question', duration: 2, eventName: 'QUESTION!' },
          { label: '\u2705 Right', color: '#4caf50', type: 'briefcase:right', duration: 2, eventName: 'CORRECT! +3' },
          { label: '\u274c Wrong', color: '#f44336', type: 'briefcase:wrong', duration: 2, eventName: 'WRONG!' },
          { label: 'Suspense', color: '#e6c200', type: 'briefcase:suspense', duration: 4, eventName: 'SUSPENSE...' },
          { label: 'Reveal: $$$', color: '#4caf50', type: 'briefcase:reveal-good', duration: 3, eventName: 'JACKPOT! +5' },
          { label: 'Reveal: Bad', color: '#455a64', type: 'briefcase:reveal-bad', duration: 3, eventName: 'ONLY 1...' },
          { label: 'Steal', color: '#f44336', type: 'game:briefcase-steal', duration: 4, eventName: 'STOLEN!' },
          { label: 'Swap', color: '#ff8c00', type: 'game:briefcase-swap', duration: 4, eventName: 'SWAP!' },
          { label: 'Transfer', color: '#a020f0', type: 'game:briefcase-transfer', duration: 4, eventName: 'TRANSFER!' },
          { label: '2X Double', color: '#ffd700', type: 'briefcase:double', duration: 3, eventName: 'DOUBLE!' },
          { label: '\u00bd Half', color: '#ff5722', type: 'briefcase:half', duration: 3, eventName: 'HALVED!' },
          { label: '? Mystery', color: '#9c27b0', type: 'briefcase:mystery', duration: 3, eventName: '???' },
          { label: 'Winner', color: '#4caf50', type: 'game:winner', duration: 5, eventName: 'WINNER!' },
          { label: 'Game End', color: '#607d8b', type: 'briefcase:end', duration: 3, eventName: 'GAME OVER' }
        ]}
      />
    </div>
  );
};

export default AdminBriefcaseBlitz;
